import { createContext, useContext, useEffect, useState } from "react"
import type { ReactNode } from "react"
import type { Session, User } from "@supabase/supabase-js"

import { supabase } from "@/lib/supabase"
import { reportError, Sentry } from "@/lib/observability"

const MOCK_MODE = import.meta.env.VITE_USE_MOCK_DATA === "true"

if (import.meta.env.PROD && MOCK_MODE) {
  throw new Error("VITE_USE_MOCK_DATA must not be enabled in production")
}

// A stand-in signed-in identity for local dev/review with no live
// Supabase project -- shaped just enough like a real Supabase `User` for
// every consumer (RequireAuth, the sign-out button) to work unmodified.
const MOCK_USER = { id: "mock-user", email: "demo@example.com" } as User

interface AuthContextValue {
  session: Session | null
  user: User | null
  loading: boolean
  signOut: () => Promise<void>
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(!MOCK_MODE)

  useEffect(() => {
    if (MOCK_MODE) return
    supabase.auth
      .getSession()
      .then(({ data }) => {
        setSession(data.session)
        setLoading(false)
      })
      .catch((err) => {
        // A rejected fetch (offline, corrupt stored refresh token) must
        // still clear loading -- otherwise RequireAuth's spinner spins
        // forever with no way out. Treat it the same as "no session."
        reportError(err, { source: "auth.bootstrap" })
        setLoading(false)
      })
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession)
    })
    return () => subscription.subscription.unsubscribe()
  }, [])

  const user = MOCK_MODE ? MOCK_USER : session?.user ?? null

  useEffect(() => {
    if (MOCK_MODE) return
    if (user) {
      Sentry.setUser({ id: user.id, email: user.email ?? undefined })
    } else {
      Sentry.setUser(null)
    }
  }, [user?.id, user?.email])

  const value: AuthContextValue = MOCK_MODE
    ? { session: null, user: MOCK_USER, loading: false, signOut: async () => {} }
    : {
        session,
        user,
        loading,
        signOut: async () => {
          await supabase.auth.signOut()
        },
      }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error("useAuth must be used within AuthProvider")
  return context
}
